import React, { useState, useEffect } from "react";
import { Plus, ShieldOff, Trash2 } from "lucide-react";
import PageLayout from "../../components/PageLayout";
import Modal from "../../components/Modal";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useAuth } from "../../context/AuthContext";
import { useClientPagination } from "../../hooks/useClientPagination";
import { visitorBlacklist as api, type VisitorBlacklistEntry, type ApiError } from "../../services/api";
import DataGrid from "../../components/DataGrid";

const inp = {
  width: "100%",
  padding: "9px 12px",
  background: "var(--nx-surface-3)",
  border: "1px solid var(--nx-border)",
  borderRadius: 8,
  fontSize: 13,
  color: "var(--nx-text-primary)",
  outline: "none",
  boxSizing: "border-box" as const,
};
const lbl = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--nx-text-secondary)",
  marginBottom: 5,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
};

const INIT = { name: "", phone: "", reason: "" };

const BlacklistPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;

  const [items, setItems] = useState<VisitorBlacklistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState(INIT);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmEntry, setConfirmEntry] = useState<VisitorBlacklistEntry | null>(null);
  const [deleteError, setDeleteError] = useState("");

  const { rows, page, setPage, totalPages } = useClientPagination(items, 20);

  useEffect(() => {
    if (!societyId) { setLoading(false); return; }
    api.list(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
  }, [societyId]);

  const openAdd = () => { setFormError(""); setForm(INIT); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setForm(INIT); setFormError(""); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setSaving(true); setFormError("");
    try {
      const created = await api.create(societyId, { name: form.name, phone: form.phone, reason: form.reason || undefined });
      setItems(p => [created, ...p]);
      closeForm();
    } catch (err) {
      setFormError((err as ApiError).message || "Failed to add blacklist entry.");
    } finally { setSaving(false); }
  };

  const handleDelete = (entry: VisitorBlacklistEntry) => { setConfirmEntry(entry); setDeleteError(""); };

  const confirmDelete = async () => {
    if (!societyId || !confirmEntry) return;
    setDeletingId(confirmEntry.id);
    try {
      await api.delete(societyId, confirmEntry.id);
      setItems(p => p.filter(e => e.id !== confirmEntry.id));
      setConfirmEntry(null);
    } catch { setDeleteError("Failed to remove blacklist entry."); }
    finally { setDeletingId(null); }
  };

  return (
    <PageLayout>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "Syne, sans-serif", fontSize: 22, fontWeight: 700, color: "var(--nx-text-primary)", letterSpacing: "-0.03em", marginBottom: 4 }}>
            Blacklist
          </h1>
          <p style={{ fontSize: 13, color: "var(--nx-text-muted)" }}>
            {items.length} blocked numbers — blocked at visitor creation and gate check-in
          </p>
        </div>
        <button onClick={openAdd} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
          <Plus size={14} /> Add to Blacklist
        </button>
      </div>

      <div className="nx-card" style={{ padding: 0, overflow: "hidden" }}>
        <DataGrid
          columns={[
            { key: "name", header: "Name", cellStyle: { fontWeight: 500 }, render: (e: VisitorBlacklistEntry) => e.name },
            { key: "phone", header: "Phone", cellStyle: { color: "var(--nx-text-secondary)" }, render: (e: VisitorBlacklistEntry) => e.phone },
            { key: "reason", header: "Reason", cellStyle: { color: "var(--nx-text-muted)" }, render: (e: VisitorBlacklistEntry) => e.reason || "—" },
            {
              key: "actions", header: "",
              cellStyle: { textAlign: "right", whiteSpace: "nowrap" },
              render: (e: VisitorBlacklistEntry) => (
                <button
                  onClick={() => handleDelete(e)}
                  disabled={deletingId === e.id}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 6px", borderRadius: 6, color: "#EF4444", opacity: deletingId === e.id ? 0.4 : 1, display: "inline-flex", alignItems: "center" }}
                  title="Remove from blacklist"
                >
                  <Trash2 size={14} />
                </button>
              ),
            },
          ]}
          rows={rows}
          rowKey={e => e.id}
          loading={loading}
          emptyIcon={<ShieldOff size={32} style={{ color: "var(--nx-text-muted)" }} />}
          emptyText="No blacklisted numbers."
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>

      <ConfirmDialog
        open={confirmEntry !== null}
        variant="danger"
        title={`Remove "${confirmEntry?.name}" from blacklist?`}
        message="This phone number will be allowed at the gate again."
        confirmLabel="Remove"
        isLoading={deletingId !== null}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => { setConfirmEntry(null); setDeleteError(""); }}
      />

      {showForm && (
        <Modal
          title="Add to Blacklist"
          submitLabel="Add to Blacklist"
          error={formError}
          isLoading={saving}
          onClose={closeForm}
          onSubmit={handleSubmit}
        >
          <div>
            <label style={lbl}>Name *</label>
            <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required placeholder="Name on record" style={inp} autoFocus />
          </div>
          <div>
            <label style={lbl}>Phone *</label>
            <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} required placeholder="9876543210" style={inp} />
          </div>
          <div>
            <label style={lbl}>Reason</label>
            <input value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))} placeholder="Optional notes" style={inp} />
          </div>
        </Modal>
      )}
    </PageLayout>
  );
};

export default BlacklistPage;
